import {
    DatePicker as KendoDatePicker,
    DatePickerProps as KendoDatePickerProps,
} from '@progress/kendo-react-dateinputs';
import { Popup, PopupProps } from '@progress/kendo-react-popup';
import { DateTimeWrapper } from 'components/date_time/DateTimeWrapper';
import { useUserDateTimeFormat } from 'custom_hooks/useUserDateTimeFormat';
import { forwardRef, Ref, useMemo } from 'react';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';
import { kendoPopupZIndex } from 'styles/variables';
import { updateRef } from 'utils/mergeRefs';

const StyledKendoDatePickerPopup = styled(Popup)`
    &.k-animation-container {
        background: ${tokens.background.paperElevation8};
        box-shadow: ${tokens.elevation['200']};
        border-radius: 3px;
        overflow: hidden;
    }

    .k-popup {
        border-radius: 3px;
        overflow: hidden;
    }

    .k-calendar .k-calendar-navigation {
        background-color: ${tokens.background.paperElevation8};
    }
    .k-calendar-navigation::before,
    .k-calendar-navigation::after {
        box-shadow: 0 0 6em 3em ${tokens.background.paperElevation8};
    }

    .k-panelbar,
    .k-popup,
    .k-widget,
    .k-input {
        background: ${tokens.background.paperElevation8};
    }

    .k-calendar .k-calendar-navigation-highlight {
        background-color: ${tokens.background.paperElevation8};
    }

    .k-calendar .k-calendar-view th,
    .k-calendar .k-calendar-view .k-calendar-th,
    .k-calendar .k-meta-header,
    .k-calendar .k-month-header {
        color: ${tokens.text.primary};
    }
    .k-calendar-infinite .k-calendar-view::after,
    .k-calendar-infinite .k-calendar-monthview::after {
        box-shadow: 0 0 2.85714em 1.42857em ${tokens.background.paperElevation8};
    }

    .k-calendar .k-calendar-td:hover .k-link {
        background-color: ${tokens.action.hover};
        color: ${tokens.text.primary};
    }

    .k-calendar .k-link {
        color: ${tokens.text.primary};
    }

    .k-calendar .k-calendar-td.k-selected .k-link,
    .k-calendar .k-calendar-td.k-active .k-link {
        background-color: ${tokens.primary.default};
        color: ${tokens.on.primary};
    }

    .k-calendar .k-calendar-td.k-selected:hover .k-link,
    .k-calendar .k-calendar-td.k-active:hover .k-link {
        background-color: ${tokens.primary.hover};
        color: ${tokens.on.primary};
    }

    .k-calendar .k-calendar-navigation li:hover,
    .k-calendar .k-calendar-navigation li.k-state-hover {
        color: ${tokens.primary.default};
    }
`;

export type DatePickerProps = KendoDatePickerProps &
    Readonly<{
        popupRef?: Ref<HTMLDivElement>;
    }>;

const DatePicker = forwardRef(({ format, popupRef, ...rest }: DatePickerProps, ref) => {
    const { kendoDateFormat } = useUserDateTimeFormat();

    const CustomPopup = useMemo(
        () =>
            ({ children, style, ...popupProps }: PopupProps) => (
                <StyledKendoDatePickerPopup
                    style={{ zIndex: kendoPopupZIndex, ...(style ?? {}) }}
                    {...popupProps}
                >
                    <div ref={(elem) => updateRef(popupRef, elem)}>{children}</div>
                </StyledKendoDatePickerPopup>
            ),
        [popupRef],
    );
    return (
        <DateTimeWrapper $hasValue={!!rest.value}>
            <KendoDatePicker
                ref={(picker) => updateRef(ref, picker?.dateInput?.element)}
                format={kendoDateFormat}
                formatPlaceholder="formatPattern"
                placeholder="Choose date"
                valid
                popup={CustomPopup}
                {...rest}
            />
        </DateTimeWrapper>
    );
});

export { DatePicker };
